// ============================================
// StaffAnonymousChatWindow Component
// Chat window for staff to reply to anonymous users
// ============================================

import React, { useEffect, useRef, useState } from 'react';
import { useChatContext } from '@/context/ChatContext';
import { useChatMessages } from '@/hooks/useChat';
import { useAnonymousGroupChat } from '@/hooks/useAnonymousGroupChat';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { X, ChevronDown, Maximize2, Send, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getAvatarInitials } from '@/lib/utils';
import { format } from 'date-fns';
import { supabase } from '@/lib/supabase/client';
import { markAnonymousSupportSessionRead, sendGroupMessage } from '@/lib/supabase/chat';

interface StaffAnonymousChatWindowProps {
  window: {
    id: string;
    title: string;
    avatarUrl?: string;
    isAnonymous: true;
    anonymousSessionId: string;
    groupId?: string;
    anonymousStoreId?: string;
    isMinimized?: boolean;
  };
  index: number;
}

export const StaffAnonymousChatWindow: React.FC<StaffAnonymousChatWindowProps> = ({ window, index }) => {
  const { closeChatWindow, minimizeChatWindow, maximizeChatWindow } = useChatContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const textInputRef = useRef<HTMLTextAreaElement>(null);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isMinimizing, setIsMinimizing] = useState(false);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [sendError, setSendError] = useState<string | null>(null);
  const [resolvedStoreAvatarUrl, setResolvedStoreAvatarUrl] = useState<string | null>(window.avatarUrl || null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Get or create group chat for this anonymous session
  const { groupChatId, isCreating: isCreatingGroupChat } = useAnonymousGroupChat({
    anonymousSessionId: window.anonymousSessionId,
    storeId: window.anonymousStoreId,
  });
  const effectiveGroupChatId = window.groupId || groupChatId || null;

  // Use standard group chat messages hook for staff to read group messages
  const { messages, readByMessageId, loading, markAsRead } = useChatMessages({
    groupId: effectiveGroupChatId || undefined,
  });
  const isAnonymousConversation = Boolean(window.isAnonymous || window.anonymousSessionId);
  const isLoadingMessages = isCreatingGroupChat || (Boolean(effectiveGroupChatId) && loading);

  const effectiveAvatarUrl = resolvedStoreAvatarUrl || window.avatarUrl || null;

  useEffect(() => {
    let cancelled = false;

    const loadCurrentUser = async () => {
      const { data, error } = await supabase.auth.getUser();
      if (!cancelled && !error) {
        setCurrentUserId(data.user?.id ?? null);
      }
    };

    void loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadStoreAvatar = async () => {
      if (!window.anonymousStoreId) {
        setResolvedStoreAvatarUrl(window.avatarUrl || null);
        return;
      }

      const { data, error } = await supabase
        .from('stores')
        .select('avatar_url')
        .eq('id', window.anonymousStoreId)
        .maybeSingle();

      if (cancelled) return;

      if (!error && data?.avatar_url) {
        setResolvedStoreAvatarUrl(data.avatar_url);
        return;
      }

      setResolvedStoreAvatarUrl(window.avatarUrl || null);
    };

    void loadStoreAvatar();

    return () => {
      cancelled = true;
    };
  }, [window.anonymousStoreId, window.avatarUrl]);

  const isTouchInputDevice = () => {
    if (typeof window === 'undefined') return false;
    const hasTouchPoints = navigator.maxTouchPoints > 0;
    const coarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
    return Boolean(hasTouchPoints || coarsePointer);
  };

  // Auto-scroll to bottom
  useEffect(() => {
    if (shouldScrollToBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages, shouldScrollToBottom]);

  useEffect(() => {
    if (!window.anonymousSessionId) return;

    const markSessionRead = async () => {
      try {
        await markAnonymousSupportSessionRead(window.anonymousSessionId);
      } catch {
        // Legacy endpoint may not be available in all environments.
      }
    };

    void markSessionRead();
  }, [window.anonymousSessionId]);

  useEffect(() => {
    if (!messages.length) return;
    void markAsRead();
  }, [messages.length, markAsRead]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    setSendError(null);
    if (!messageInput.trim() || sending || !effectiveGroupChatId) return;
    
    try {
      setSending(true);
      setShouldScrollToBottom(true);
      // Send message to group chat (without is_anonymous flag, so it appears as staff message)
      await sendGroupMessage({
        group_id: effectiveGroupChatId,
        content: messageInput.trim(),
        type: 'text',
      });
      setMessageInput('');
      if (textInputRef.current) {
        textInputRef.current.style.height = 'auto';
      }
    } catch (error: any) {
      setSendError(error?.message || 'Error al enviar el mensaje');
      console.error('Error sending message:', error);
    } finally {
      setSending(false);
    }
  };

  const handleMinimize = () => {
    setIsMinimizing(true);
    setTimeout(() => {
      minimizeChatWindow(window.id);
      setIsMinimizing(false);
    }, 150);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      closeChatWindow(window.id);
      setIsClosing(false);
    }, 150);
  };

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessageInput(e.target.value);
    if (textInputRef.current) {
      textInputRef.current.style.height = 'auto';
      textInputRef.current.style.height = Math.min(textInputRef.current.scrollHeight, 120) + 'px';
    }
  };

  const isMobile = typeof globalThis.window !== 'undefined' && globalThis.window.innerWidth < 1024;
  const [mobileNavbarHeight, setMobileNavbarHeight] = useState<number>(0);

  useEffect(() => {
    const updateNavbarHeight = () => {
      try {
        const el = document.querySelector('.app-bottom-nav') as HTMLElement | null;
        const h = el ? el.getBoundingClientRect().height : 0;
        setMobileNavbarHeight(h);
      } catch {
        setMobileNavbarHeight(0);
      }
    };
    updateNavbarHeight();
    if (typeof globalThis !== 'undefined' && (globalThis as any).addEventListener) {
      (globalThis as any).addEventListener('resize', updateNavbarHeight);
      return () => (globalThis as any).removeEventListener('resize', updateNavbarHeight);
    }
  }, []);

  const computedZIndex = 10005 - index;
  const minimizedZIndex = computedZIndex;
  const rightOffset = !isMobile ? 16 + (index * 320) : undefined;
  const bottomOffset = 16;

  if (window.isMinimized) {
    return (
      <div
        className={cn(
          'fixed bottom-0 w-80 bg-background border rounded-t-lg shadow-lg max-md:fixed max-md:w-full max-md:left-0 max-md:right-0 max-md:rounded-none',
          'transition-all duration-200',
          isClosing && 'animate-out slide-out-to-bottom duration-200'
        )}
        style={{
          right: isMobile ? undefined : `${rightOffset}px`,
          bottom: `${bottomOffset + (isMobile ? mobileNavbarHeight : 0)}px`,
          zIndex: minimizedZIndex,
        }}
        data-chat-window
      >
        <div
          role="button"
          onClick={() => maximizeChatWindow(window.id)}
          className="w-full p-3 flex items-center justify-between transition-colors rounded-t-lg max-md:rounded-none hover:bg-muted/50 cursor-pointer"
        >
          <div className="flex items-center gap-3">
            <Avatar className="w-8 h-8">
              <AvatarImage src={effectiveAvatarUrl || undefined} />
              <AvatarFallback>{getAvatarInitials('Anon')}</AvatarFallback>
            </Avatar>
            <span className="font-medium">Anónimo</span>
          </div>
          <button
            type="button"
            className="p-1 bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'fixed flex flex-col bg-background shadow-xl border-t overflow-hidden',
        'w-80 h-[500px] md:h-[500px] rounded-t-lg border-x',
        'max-md:fixed max-md:right-0 max-md:w-full max-md:rounded-none max-md:border-none',
        isClosing || isMinimizing
          ? 'animate-out slide-out-to-bottom duration-200'
          : 'animate-in slide-in-from-bottom duration-200'
      )}
      style={{
        right: isMobile ? 0 : `${rightOffset}px`,
        bottom: 0,
        top: isMobile ? 0 : 'auto',
        height: isMobile ? '100dvh' : '500px',
        zIndex: computedZIndex,
        backgroundColor: 'hsl(var(--background))',
      }}
      data-chat-window
    >
      {/* Safe Area Spacer for Mobile */}
      {isMobile && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b bg-primary text-white min-h-[64px] shrink-0 overflow-hidden">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Avatar className="w-8 h-8 rounded">
            <AvatarImage src={effectiveAvatarUrl || undefined} />
            <AvatarFallback>{getAvatarInitials('Anon')}</AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <p className="font-bold truncate !text-white select-none">Usuario anónimo</p>
              <span className="inline-flex items-center rounded-full border border-white/40 bg-white/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white/95 shrink-0">
                Soporte
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <div
            role="button"
            className="h-8 w-8 text-white hover:bg-white/10 flex items-center justify-center rounded cursor-pointer transition-colors"
            onClick={handleMinimize}
          >
            <ChevronDown className="w-4 h-4" />
          </div>
          <button
            type="button"
            className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
            onClick={(e) => {
              e.stopPropagation();
              handleClose();
            }}
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 px-4" ref={scrollAreaRef}>
        {isLoadingMessages ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground">Cargando mensajes...</p>
          </div>
        ) : !effectiveGroupChatId ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground text-center">
              No se pudo resolver este chat anónimo.<br />
              <span className="text-sm">Intenta cerrarlo y abrirlo de nuevo desde la lista.</span>
            </p>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-muted-foreground text-center">
              No hay mensajes aún.<br />
              <span className="text-sm">¡Envía el primero!</span>
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 py-4">
            {/* Pinned Session Info Block */}
            {(() => {
              // Buscar IP y ubicación más recientes (de cualquier mensaje anónimo)
              let latestIp: string | null = null;
              let latestLocation: string | null = null;

              for (let i = messages.length - 1; i >= 0; i--) {
                const meta = (messages[i] as any).metadata || {};
                if (meta?.is_anonymous === true || meta?.anonymous_session_id) {
                  if (!latestIp) {
                    latestIp = meta?.ip || meta?.ip_address || meta?.ipAddress || null;
                  }
                  if (!latestLocation) {
                    latestLocation = meta?.location || meta?.city || null;
                  }
                  if (latestIp && latestLocation) break;
                }
              }

              return (
                <div className="bg-primary/10 border border-primary/30 rounded-lg p-3 mb-2">
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Session ID</span>
                      <span className="text-xs font-mono bg-background/60 px-2 py-1 rounded text-foreground truncate">{window.anonymousSessionId.slice(0, 8)}...</span>
                    </div>
                    {latestIp && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">IP</span>
                        <span className="text-xs font-mono bg-background/60 px-2 py-1 rounded text-foreground">{latestIp}</span>
                      </div>
                    )}
                    {latestLocation && (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Ubicación</span>
                        <span className="text-xs bg-background/60 px-2 py-1 rounded text-foreground truncate">{latestLocation}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {messages.map((message, idx) => {
              const metadata = (message as any).metadata || {};
              // Para el staff visualizando mensajes del grupo anónimo:
              // - Mensajes con metadata.is_anonymous = true o anonymous_session_id presente → usuario anónimo (LEFT/incoming)
              // - Otros mensajes (sin esa flag) → son del staff (RIGHT/outgoing)
              // - Mensajes con metadata.is_system = true → son mensajes del sistema (CENTER)
              const hasAnonymousSession = Boolean(metadata?.anonymous_session_id);
              const isAnonymousMessage = metadata?.is_anonymous === true || hasAnonymousSession;
              const isSystemMessage = metadata?.is_system === true;
              const isOwnStaffMessage = !isAnonymousMessage && !!currentUserId && message.sender_id === currentUserId;
              const seenByUsers = (readByMessageId[message.id] || [])
                .filter((reader) => reader.userId !== message.sender_id)
                .slice(0, 5);

              const anonymousName =
                metadata?.anonymous_name ||
                metadata?.name ||
                metadata?.display_name ||
                (hasAnonymousSession ? 'Usuario anónimo' : null);

              const anonymousIp =
                metadata?.ip ||
                metadata?.ip_address ||
                metadata?.ipAddress ||
                null;
              
              const showTimestamp =
                idx === 0 ||
                new Date(message.created_at).getTime() -
                  new Date(messages[idx - 1].created_at).getTime() >
                  300000; // 5 minutes
              
              return (
                <div key={message.id}>
                  {showTimestamp && (
                    <div className="text-center text-xs text-muted-foreground my-2">
                      {format(new Date(message.created_at), 'dd/MM/yyyy HH:mm')}
                    </div>
                  )}
                  {isSystemMessage ? (
                    <div className="flex justify-center">
                      <div className="bg-muted/50 text-muted-foreground text-xs px-3 py-2 rounded-full">
                        {message.content || message.message}
                      </div>
                    </div>
                  ) : (
                    <div className={cn('flex w-full', isOwnStaffMessage ? 'justify-end' : 'justify-start')}>
                      <div className={cn('flex flex-col max-w-[70%]', isOwnStaffMessage ? 'items-end' : 'items-start')}>
                        <div
                          className={cn(
                            'w-full rounded-lg px-3 py-2 border',
                            isOwnStaffMessage
                              ? 'bg-primary/15 text-foreground border-primary/35 rounded-tr-sm ml-auto'
                              : 'bg-muted border-muted mr-auto'
                          )}
                        >
                          {isAnonymousMessage && (anonymousName || anonymousIp) && (
                            <p className="text-[10px] leading-4 opacity-70 mb-1">
                              {[anonymousName, anonymousIp ? `IP ${anonymousIp}` : null].filter(Boolean).join(' · ')}
                            </p>
                          )}
                          <p className="text-sm whitespace-pre-wrap break-words">
                            {message.content || message.message}
                          </p>
                        </div>
                        {isOwnStaffMessage && seenByUsers.length > 0 && (
                          <div className="flex items-center justify-end gap-1 px-1 mt-1">
                            <span className="text-[10px] uppercase tracking-wide text-muted-foreground/70">Visto</span>
                            <div className="flex flex-row-reverse items-center">
                              {seenByUsers.map((reader) => {
                                const readerName = reader.displayName || reader.username || 'Usuario';
                                return (
                                  <Avatar
                                    key={`${message.id}-${reader.userId}`}
                                    title={readerName}
                                    className="w-4 h-4 border border-background -mr-1 first:mr-0 bg-background"
                                  >
                                    <AvatarImage src={reader.avatarUrl || undefined} />
                                    <AvatarFallback className="text-[8px]">
                                      {getAvatarInitials(readerName)}
                                    </AvatarFallback>
                                  </Avatar>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Input */}
      <form onSubmit={handleSendMessage} className="flex gap-2 p-3 border-t bg-background shrink-0">
        <textarea
          ref={textInputRef}
          value={messageInput}
          onChange={handleTextareaChange}
          onKeyDown={(e) => {
            if (isTouchInputDevice()) {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSendMessage(e as any);
              }
              return;
            }

            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage(e as any);
            }
          }}
          placeholder="Escribe una respuesta..."
          disabled={sending || isLoadingMessages || !effectiveGroupChatId}
          className="flex-1 resize-none border rounded-lg px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[40px] max-h-[120px]"
          rows={1}
        />
        <Button
          type="submit"
          disabled={sending || !messageInput.trim()}
          size="icon"
          className="h-10 w-10 shrink-0"
          aria-label="Enviar mensaje"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </form>

      {/* Error feedback */}
      {sendError && (
        <div className="px-3 py-2 text-center bg-destructive/10 border-t text-xs text-destructive">
          {sendError}
        </div>
      )}
      {/* Info badge */}
      <div className="px-3 py-2 text-center bg-muted/50 border-t text-xs text-muted-foreground">
        Respondiendo a usuario anónimo
      </div>
    </div>
  );
};

export default StaffAnonymousChatWindow;
